import "@/App.css";
import { useEffect, useState } from "react";
import { preparaAudio } from "@/lib/voce";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { I18nProvider } from "@/i18n";
import Landing from "@/pages/Landing";
import { AppProvider, useApp } from "@/context/AppContext";
import { PlanProvider, usePlan } from "@/context/PlanContext";
import { Layout } from "@/components/Layout";
import { UpgradeDialog } from "@/components/UpgradeDialog";
import RoleSelect from "@/pages/RoleSelect";
import Sala from "@/pages/Sala";
import Comanda from "@/pages/Comanda";
import Monitor from "@/pages/Monitor";
import Direttore from "@/pages/Direttore";
import Serata from "@/pages/Serata";
import Movimenti from "@/pages/Movimenti";
import Statistiche from "@/pages/Statistiche";
import DolciPesce from "@/pages/DolciPesce";
import Prenotazioni from "@/pages/Prenotazioni";
import Clienti from "@/pages/Clienti";
import MenuPage from "@/pages/MenuPage";
import PiattiGiorno from "@/pages/PiattiGiorno";
import MenuFisso from "@/pages/MenuFisso";
import CassaFiscale from "@/pages/CassaFiscale";
import Carta from "@/pages/Carta";
import Dipendenti from "@/pages/Dipendenti";
import Turni from "@/pages/Turni";
import Piantina from "@/pages/Piantina";
import Impostazioni from "@/pages/Impostazioni";
import Colori from "@/pages/Colori";
import Ritiri from "@/pages/Ritiri";
import Stampanti from "@/pages/Stampanti";
import Spesa from "@/pages/Spesa";
import Abbonamento from "@/pages/Abbonamento";
import Magazzino from "@/pages/Magazzino";
import Console from "@/pages/Console";
import TenantLogin from "@/pages/TenantLogin";
import Sospeso from "@/pages/Sospeso";
import Asporto from "@/pages/Asporto";
import Storico from "@/pages/Storico";
import Setup from "@/pages/Setup";
import Funzioni from "@/pages/Funzioni";
import Delivery from "@/pages/Delivery";
import Cassa from "@/pages/Cassa";
import Sprechi from "@/pages/Sprechi";
import Backup from "@/pages/Backup";
import CasoStudio from "@/pages/CasoStudio";
import Manuale from "@/pages/Manuale";
import Guida from "@/pages/Guida";
import Legale from "@/pages/Legale";
import ComeFunziona from "@/pages/ComeFunziona";
import Garanzie from "@/pages/Garanzie";
import PersonaleEntra from "@/pages/PersonaleEntra";
import Biglietto from "@/pages/Biglietto";
import Caparra from "@/pages/Caparra";
import GuidaDemo from "@/pages/GuidaDemo";
import Attesa from "@/pages/Attesa";
import DemoEntra from "@/pages/DemoEntra";
import { OfflineBanner } from "@/components/OfflineBanner";
import { Avvio } from "@/components/Avvio";
import { FirmaLegale } from "@/components/FirmaLegale";

const TOAST_MOBILE_OFFSET = { bottom: 80 };

const Shell = () => {
  const { user, staff, tenant, setTenant, setupDone, refresh, settings, demoMode } = useApp();
  const { plan } = usePlan();
  // Prima si legge e si firma, poi si configura: il titolare non passa oltre senza firma
  const [firmato, setFirmato] = useState(false);
  const [showLogin, setShowLogin] = useState(window.location.pathname === "/entra");
  // Da dove arriva chi apre il link (QR del biglietto, immagine WhatsApp, locandina)
  useEffect(() => {
    const src = new URLSearchParams(window.location.search).get("src");
    if (!src || sessionStorage.getItem("rh_src") === src) return;
    sessionStorage.setItem("rh_src", src);
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/scan`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ src, sid: sessionStorage.getItem("rh_quiz_sid") || "" }),
    }).catch(() => {});
  }, []);
  const legalPath = { "/termini": "termini", "/privacy": "privacy", "/stato": "stato", "/sicurezza": "sicurezza" }[window.location.pathname];
  if (legalPath) return <Legale tipo={legalPath} />;
  if (window.location.pathname === "/esci") {
    // Via d'uscita d'emergenza: chi resta agganciato a una demo apre /esci e torna al sito pulito
    localStorage.removeItem("rh_tenant_token");
    localStorage.removeItem("rh_staff_session");
    localStorage.removeItem("rh_user");
    window.location.replace("/");
    return <Avvio nome="Reprehendo" nota="Esco dalla demo…" />;
  }
  if (window.location.pathname === "/come-funziona") return <ComeFunziona />;
  if (window.location.pathname === "/garanzie") return <Garanzie />;
  if (window.location.pathname === "/personale") return <PersonaleEntra />;
  if (window.location.pathname === "/biglietto") return <Biglietto />;
  if (window.location.pathname === "/caparra") return <Caparra />;  if (window.location.pathname.startsWith("/carta/")) return <Carta />;
  if (window.location.pathname === "/guida-demo") return <GuidaDemo />;
  if (window.location.pathname === "/rh-console") return <Console />;
  if (window.location.pathname === "/demo" || window.location.pathname.startsWith("/demo/")
      || window.location.pathname === "/vetrina" || window.location.pathname.startsWith("/vetrina/")) return <DemoEntra />;
  if (!tenant)
    return showLogin ? (
      <TenantLogin onLogged={setTenant} onBack={() => setShowLogin(false)} />
    ) : (
      <Landing onEnter={() => setShowLogin(true)} startQuiz={["/prova", "/prova-demo"].includes(window.location.pathname)} />
    );
  if (plan?.suspended) return <Sospeso />;
  if (!staff.length) return <Avvio nome={settings?.business_name} />;
  if (!user) return <RoleSelect />;
  if (user.role === "titolare" && !demoMode && !firmato)
    return <FirmaLegale onFirmato={() => setFirmato(true)} attivita={settings?.business_name} />;
  if (!setupDone && user.role === "titolare") return <Setup onDone={refresh} />;

  const owner = user.role === "titolare";
  const home = user.can_send === false ? <Monitor /> : owner ? <Serata /> : user.role === "direttore" ? <Direttore /> : <Sala />;

  return (
    <Layout>
      <OfflineBanner />
      <Routes>
        <Route path="/" element={home} />
        <Route path="/sala" element={user.can_send === false ? <Navigate to="/monitor" /> : <Sala />} />
        <Route path="/asporto" element={<Asporto />} />
        <Route path="/cassa" element={<Cassa />} />
        <Route path="/attesa" element={<Attesa />} />
        {owner && <Route path="/sprechi" element={<Sprechi />} />}
        {(owner || user.role === "manutentore") && <Route path="/backup" element={<Backup />} />}
        <Route path="/comanda/:id" element={<Comanda />} />
        <Route path="/monitor" element={<Monitor />} />
        <Route path="/direttore" element={user.role === "cameriere" ? <Navigate to="/sala" /> : <Direttore />} />
        {owner && <Route path="/movimenti" element={<Movimenti />} />}
        {owner && <Route path="/statistiche" element={<Statistiche />} />}
        {owner && <Route path="/storico" element={<Storico />} />}
        {owner && <Route path="/magazzino" element={<Magazzino />} />}
        {owner && <Route path="/dolci-pesce" element={<DolciPesce />} />}
        <Route path="/prenotazioni" element={<Prenotazioni />} />
        {owner && <Route path="/clienti" element={<Clienti />} />}
        {owner && <Route path="/menu" element={<MenuPage />} />}
        {owner && <Route path="/menu-fisso" element={<MenuFisso />} />}
        <Route path="/piatti-giorno" element={<PiattiGiorno />} />
        {owner && <Route path="/cassa-fiscale" element={<CassaFiscale />} />}
        {owner && <Route path="/dipendenti" element={<Dipendenti />} />}
        <Route path="/turni" element={<Turni />} />
        {owner && <Route path="/piantina" element={<Piantina />} />}
        {owner && <Route path="/impostazioni" element={<Impostazioni />} />}
        <Route path="/colori" element={<Colori />} />
        <Route path="/ritiri" element={<Ritiri />} />
        {owner && <Route path="/funzioni" element={<Funzioni />} />}
        {owner && <Route path="/delivery-guida" element={<Delivery />} />}
        {owner && <Route path="/stampanti" element={<Stampanti />} />}
        {owner && <Route path="/spesa" element={<Spesa />} />}
        {owner && <Route path="/caso-studio" element={<CasoStudio onBack={() => window.history.back()} />} />}
        {owner && <Route path="/abbonamento" element={<Abbonamento />} />}
        {owner && <Route path="/payment/success" element={<Abbonamento />} />}
        {owner && <Route path="/payment/cancel" element={<Abbonamento />} />}
        <Route path="/manuale" element={<Manuale />} />
        <Route path="/guida" element={<Guida />} />
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
      <UpgradeDialog />
    </Layout>
  );
};

export default function App() {
  // Sul telefono gli avvisi arrivano dal basso: in alto coprirebbero l'intestazione
  const [mobile, setMobile] = useState(() => window.matchMedia("(max-width: 640px)").matches);
  // iPhone tiene l'audio spento finché non si tocca lo schermo: al primo tocco lo sblocchiamo,
  // così la campanella della comanda pronta si sente davvero.
  useEffect(() => { preparaAudio(); }, []);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 640px)");
    const onChange = (e) => setMobile(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  return (
    <I18nProvider>
      <PlanProvider>
        <AppProvider>
          <BrowserRouter>
            <Shell />
          </BrowserRouter>
          <Toaster position={mobile ? "bottom-center" : "top-right"} richColors closeButton visibleToasts={mobile ? 2 : 3} mobileOffset={TOAST_MOBILE_OFFSET} />
        </AppProvider>
      </PlanProvider>
    </I18nProvider>
  );
}
